import React, { useEffect, useState } from "react";
import Icon from "../../assets/icons/popup/E-Lotto Logo.svg?react";
import Blue from "../../assets/icons/popup/blue-apple.svg?react";
import White from "../../assets/icons/popup/white-apple.svg?react";
import { X } from "lucide-react";

export default function AppInstallPopup({ isOpen, onClose }) {
  const [isVisible, setIsVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOSDevice, setIsIOSDevice] = useState(false);

  // Detect iOS devices 
  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOSDevice(isIOS);
  }, []);

  // Capture the install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event so it can be triggered later
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Handle visibility
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setIsVisible(true), 0); // Trigger animation
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  // Install the PWA
  const handleInstallClick = async () => {
    if (!deferredPrompt && !isIOSDevice) {
      // The app is already installed or not installable
      onClose();
      return;
    }

    if (deferredPrompt) {
      // Show the native install prompt
      deferredPrompt.prompt();
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to the install prompt: ${outcome}`);
      // We no longer need the prompt. Clear it.
      setDeferredPrompt(null);
      // Close the popup
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-end justify-center">
        {/* Background Overlay */}
        <div
          className={`absolute inset-0 bg-gray-800 bg-opacity-50 backdrop-blur-sm transition-opacity duration-300 ${
            isVisible ? "opacity-100" : "opacity-0"
          }`}
          onClick={onClose} // Close popup when background is clicked
        ></div>

        {/* Popup Content */}
        <div
          className={`bg-gray-100 rounded-t-xl max-w-full w-full p-4 shadow-lg transform transition-transform duration-1000 ${
            isVisible ? "translate-y-0" : "translate-y-full"
          }`}
          style={{ 
            zIndex: 999,
            position: 'relative',
            marginBottom: 'calc(var(--safe-area-bottom, 0px) + 95px)'
          }}
        >
          <div className="flex items-start gap-3 mb-4 pl-3">
            <Icon className="rounded-xl" />
            <p className="flex-1 text-sm text-[#12131A] font-semibold">
              We would like to add it to your home screen for the latest free
              bets and bonus updates.
            </p>

            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <X />
            </button>
          </div>
          <div className="px-6">
            <hr />
          </div>

          <div className="text-center px-6">
            <button 
              className="text-[#16B9FF] text-base font-medium mb-6 underline"
              onClick={onClose}
            >
              Continue to use H5
            </button>

            <div className="relative">
              <div className="absolute -top-3 left-12 bg-red-500 text-white text-[10px] px-2 py-0.5 rounded animate-zoom">
                Enjoy more benefits!
              </div>
              <div className="flex gap-3 font-semibold">
                {isIOSDevice ? (
                  <button 
                    className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2"
                    onClick={() => {
                      alert("To install on iOS: tap the share button, then 'Add to Home Screen'");
                      onClose();
                    }}
                  >
                    ADD TO HOME SCREEN
                  </button>
                ) : (
                  <button 
                    className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2"
                    onClick={handleInstallClick}
                  >
                    INSTALL APP
                  </button>
                )}
                <button 
                  className="flex-1 border-2 border-blue-500 text-blue-500 py-2 px-4 rounded-lg flex items-center justify-center gap-2"
                  onClick={onClose}
                >
                  WEB-APP
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
